import React, { useState, useEffect } from "react";
import api, { updateOwnPaperSubmission } from '../../services/api';
import PaperBriefDescriptionEdit from "./PaperBriefDescriptionEdit";
import PaperFilesEdit from "./PaperFilesEdit";
import { countries } from "../../constants/types";

// List of academic rank/title options for dropdown
const RANK_TITLES = [
  "Professor",
  "Associate Professor", 
  "Assistant Professor",
  "Lecturer",
  "Senior Lecturer",
  "Instructor",
  "PhD Student",
  "Other"
];

/**
 * PaperEditModal Component
 * 
 * Admin modal for editing all aspects of a submitted paper.
 * Provides the same interface as paper submission but for editing existing papers.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.paper - Paper object to edit
 * @param {boolean} props.show - Whether modal is visible
 * @param {Function} props.onHide - Function to hide modal
 * @param {Function} props.onPaperUpdated - Callback when paper is successfully updated
 */
const PaperEditModal = ({ paper, show, onHide, onPaperUpdated, isUserContext = false }) => {
  const maxSection = isUserContext ? 3 : 4;
  const [currentSection, setCurrentSection] = useState(1);
  const [conferences, setConferences] = useState([]);
  const [formData, setFormData] = useState({
    manuscriptType: "",
    conferenceId: "",
    manuscriptTitle: "",
    manuscriptAbstract: "",
    researchArea1: "",
    researchArea2: "",
    researchArea3: "",
    researchArea4: "",
    researchArea5: "",
    fullManuscript: null,
    blindCopyManuscript: null,
    adminNotes: "",
  });

  const [allAuthors, setAllAuthors] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // State to track custom rank titles for authors who select "Other"
  const [customRankTitles, setCustomRankTitles] = useState({});

  // Initialize form data when paper changes
  useEffect(() => {
    if (paper && show) {
      initializeFormData();
      loadConferences();
    }
  }, [paper, show]);

  /**
   * Initialize form data with existing paper data
   */
  const initializeFormData = () => {
    setFormData({
      manuscriptType: paper.manuscriptType || "",
      conferenceId: (typeof paper.conferenceId === 'object' ? paper.conferenceId._id : paper.conferenceId) || "",
      manuscriptTitle: paper.manuscriptTitle || "",
      manuscriptAbstract: paper.manuscriptAbstract || "",
      status: paper.status || "under_review",
      researchArea1: paper.researchAreas?.[0] || "",
      researchArea2: paper.researchAreas?.[1] || "",
      researchArea3: paper.researchAreas?.[2] || "",
      researchArea4: paper.researchAreas?.[3] || "",
      researchArea5: paper.researchAreas?.[4] || "",
      fullManuscript: null,
      blindCopyManuscript: null,
      adminNotes: paper.adminNotes || "",
    });
    
    // Initialize authors
    if (paper.authors && paper.authors.length > 0) {
      const formattedAuthors = paper.authors.map((author, index) => {
        const rankTitle = author.primaryAffiliation?.title || author.primaryAffiliation?.rank || author.rankTitle || author.rank || author.primaryRank || '';
        return {
          id: author.id || `author-${index}`,
          authorId: author.authorId || '',
          isCurrentUser: author.isCurrentUser || false,
          isCorresponding: author.isCorresponding || false,
          firstName: author.firstName || '',
          lastName: author.lastName || '',
          email: author.email || '',
          institution: author.primaryAffiliation?.institution || author.institution || author.primaryInstitution || '',
          department: author.primaryAffiliation?.department || author.department || author.primaryDepartment || '',
          rankTitle,
          country: author.primaryAffiliation?.address?.country || author.country || author.primaryCountry || '',
          authorOrder: author.authorOrder || index + 1,
          request: author.request || '',
          registrationFee: author.registrationFee || '',
          currency: author.currency || '',
        };
      });
      
      // Initialize custom rank titles for authors with ranks not in predefined list
      const newCustomRankTitles = {};
      formattedAuthors.forEach(author => {
        const rank = author.rankTitle;
        if (rank && !RANK_TITLES.includes(rank)) {
          // This author has a custom rank title, set it as "Other" with custom text
          newCustomRankTitles[author.id] = rank;
          author.rankTitle = "Other";
        }
      });
      setCustomRankTitles(newCustomRankTitles);
      
      // Ensure at least one corresponding author exists
      const hasCorresponding = formattedAuthors.some(author => author.isCorresponding);
      if (!hasCorresponding && formattedAuthors.length > 0) {
        formattedAuthors[0].isCorresponding = true;
      }
      
      setAllAuthors(formattedAuthors);
    } else {
      setAllAuthors([]);
      setCustomRankTitles({});
    }

    setCurrentSection(1);
    setError("");
  };

  /**
   * Load available conferences
   */
  const loadConferences = async () => {
    try {
      const response = await api.get('/conferences');
      setConferences(response.data);
    } catch (error) {
      console.error("Error loading conferences:", error);
    }
  };

  /**
   * Handle form field changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  /**
   * Handle file changes
   */
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData({ ...formData, [name]: files[0] });
  };

  // Author management functions (same as PaperSubmission)
  const addAuthor = () => {
    const newAuthor = {
      id: Date.now() + Math.random(),
      isCurrentUser: false,
      isCorresponding: false,
      firstName: "",
      lastName: "",
      email: "",
      institution: "",
      department: "",
      rankTitle: "",
      country: "",
      authorOrder: allAuthors.length + 1,
      authorId: "",
    };
    setAllAuthors(prev => [...prev, newAuthor]);
  };

  const removeAuthor = (authorId) => {
    // Clean up custom rank title for this author
    setCustomRankTitles(prev => {
      const updated = { ...prev };
      delete updated[authorId];
      return updated;
    });
    
    setAllAuthors(prev => {
      const filtered = prev.filter(author => author.id !== authorId);
      return filtered.map((author, index) => ({
        ...author,
        authorOrder: index + 1,
      }));
    });
  };

  const updateAuthor = (authorId, field, value) => {
    setAllAuthors(prev => 
      prev.map(author => 
        author.id === authorId 
          ? { ...author, [field]: value }
          : author
      )
    );
  };

  const moveAuthorInList = (index, direction) => {
    const newAuthors = [...allAuthors];
    if (direction === 'up' && index > 0) {
      [newAuthors[index], newAuthors[index - 1]] = [newAuthors[index - 1], newAuthors[index]];
    } else if (direction === 'down' && index < newAuthors.length - 1) {
      [newAuthors[index], newAuthors[index + 1]] = [newAuthors[index + 1], newAuthors[index]];
    }
    newAuthors.forEach((author, idx) => {
      author.authorOrder = idx + 1;
    });
    setAllAuthors(newAuthors);
  };

  const setCorrespondingAuthor = (authorId) => {
    setAllAuthors(prev =>
      prev.map(author => ({
        ...author,
        isCorresponding: author.id === authorId
      }))
    );
  };

  /**
   * Validate section data
   */
  const validateSection = (section) => {
    switch (section) {
      case 1:
        return formData.manuscriptType && formData.conferenceId && 
               formData.manuscriptTitle && formData.manuscriptAbstract && 
               formData.researchArea1 && formData.researchArea2 && formData.researchArea3;
      case 2:       
        const hasRequiredFields = allAuthors.every(author => {
          const firstName = (author.firstName || '').trim();
          const lastName = (author.lastName || '').trim();
          const email = (author.email || '').trim();
          const institution = (author.institution || '').trim();
          const country = (author.country || '').trim();
          const rankTitle = (author.rankTitle || '').trim();
          
          // Basic required fields
          const basicFieldsValid = firstName && lastName && email && institution && country;// && rankTitle;
          if (!basicFieldsValid) return false;
          
          return true;
        });
        
        const hasCorrespondingAuthor = allAuthors.some(author => author.isCorresponding);
        
        return hasRequiredFields && hasCorrespondingAuthor && allAuthors.length > 0;
      case 3:
        return true; // Files are optional for editing
      case 4:
        return true; // Admin notes are optional
      default:
        return true;
    }
  };

  /**
   * Navigate between sections
   */
  const nextSection = () => {
    if (validateSection(currentSection)) {
      setCurrentSection(currentSection + 1);
      setError("");
    } else {
      setError("Please fill in all required fields correctly.");
    }
  };

  const prevSection = () => {
    setCurrentSection(currentSection - 1);
    setError("");
  };

  /**
   * Save paper changes
   */
  const handleSave = async () => {
    // Validate all sections
    for (let i = 1; i <= maxSection; i++) {
      if (!validateSection(i)) {
        setError(`Please complete all required fields in section ${i}.`);
        setCurrentSection(i);
        return;
      }
    }

    setSaving(true);
    setError("");

    try {
      const updateData = new FormData();
      
      // Add basic paper information
      updateData.append('manuscriptType', formData.manuscriptType);
      updateData.append('conferenceId', formData.conferenceId);
      updateData.append('manuscriptTitle', formData.manuscriptTitle);
      updateData.append('manuscriptAbstract', formData.manuscriptAbstract);
      updateData.append('status', formData.status);
      updateData.append('researchArea1', formData.researchArea1);
      updateData.append('researchArea2', formData.researchArea2);
      updateData.append('researchArea3', formData.researchArea3);
      updateData.append('researchArea4', formData.researchArea4);
      updateData.append('researchArea5', formData.researchArea5);
      if (!isUserContext) {
        updateData.append('adminNotes', formData.adminNotes || '');
      }
      
      // Add authors with processed rank titles
      const processedAuthors = allAuthors.map(author => {
        // If author selected "Other" for rank title, combine it with the custom title
        if (author.rankTitle === "Other" && customRankTitles[author.id]) {
          return {
            ...author,
            rankTitle: customRankTitles[author.id]
          };
        }
        return author;
      });
      
      updateData.append('authors', JSON.stringify(processedAuthors));
      
      // Add files if they were changed
      if (formData.fullManuscript) {
        updateData.append('fullManuscript', formData.fullManuscript);
      }
      if (formData.blindCopyManuscript) {
        updateData.append('blindCopyManuscript', formData.blindCopyManuscript);
      }

      if (isUserContext) {
        await updateOwnPaperSubmission(paper._id, updateData);
      } else {
        await api.put(`/admin/papers/${paper._id}`, updateData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      
      onPaperUpdated();
      onHide();
      
    } catch (err) {
      console.error('Update error:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Failed to update paper. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  /**
   * Render current section
   */
  const renderCurrentSection = () => {
    switch (currentSection) {
      case 1:
        return (
          <div>
            <PaperBriefDescriptionEdit
              formData={formData}
              handleChange={handleChange}
              conferences={conferences}
            />
          </div>
        );
      case 2:
        return (
          <div className="admin-authors-section">
            <h4 className="mb-3">Authors Management</h4>
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Admin Mode:</strong> You can edit all author details for this submission.
            </div>
            
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Authors ({allAuthors.length})</h5>
              <button type="button" className="btn btn-primary btn-sm" onClick={addAuthor}>
                <i className="bi bi-plus-circle me-1"></i>
                Add Author
              </button>
            </div>

            {allAuthors.length === 0 ? (
              <div className="text-center py-4">
                <i className="bi bi-person-plus display-4 text-muted"></i>
                <p className="text-muted mt-2">No authors found. Add authors to continue.</p>
              </div>
            ) : (
              <div className="authors-list">
                {allAuthors.map((author, index) => (
                  <div key={author.id} className="card mb-3">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        Author #{index + 1}
                        {author.isCorresponding && (
                          <span className="badge bg-primary ms-2">Corresponding</span>
                        )}
                      </h6>
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => moveAuthorInList(index, 'up')}
                          disabled={index === 0}
                          title="Move up"
                        >
                          <i className="bi bi-arrow-up"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => moveAuthorInList(index, 'down')}
                          disabled={index === allAuthors.length - 1}
                          title="Move down"
                        >
                          <i className="bi bi-arrow-down"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeAuthor(author.id)}
                          title="Remove author"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                    
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">First Name <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control"
                            value={author.firstName || ''}
                            onChange={(e) => updateAuthor(author.id, 'firstName', e.target.value)}
                            placeholder="Enter first name"
                            required
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Last Name <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control"
                            value={author.lastName || ''}
                            onChange={(e) => updateAuthor(author.id, 'lastName', e.target.value)}
                            placeholder="Enter last name"
                            required
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Rank/Title <span className="text-danger">*</span></label>
                          <select
                            className="form-select"
                            value={author.rankTitle || ''}
                            required
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              updateAuthor(author.id, 'rankTitle', selectedValue);
                              
                              // If "Other" is deselected, clear the custom title
                              if (selectedValue !== "Other" && customRankTitles[author.id]) {
                                setCustomRankTitles(prev => {
                                  const updated = { ...prev };
                                  delete updated[author.id];
                                  return updated;
                                });
                              }
                            }}
                          >
                            <option value="">Select rank/title</option>
                            {RANK_TITLES.map((title) => (
                              <option key={title} value={title}>{title}</option>
                            ))}
                          </select>
                          
                          {/* Show custom text input when "Other" is selected */}
                          {author.rankTitle === "Other" && (
                            <div className="mt-2">
                              <input
                                type="text"
                                className="form-control"
                                required={false}
                                value={customRankTitles[author.id] || ''}
                                onChange={(e) => setCustomRankTitles(prev => ({
                                  ...prev,
                                  [author.id]: e.target.value
                                }))}
                                placeholder="Please specify your rank/title"
                              />
                            </div>
                          )}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Email <span className="text-danger">*</span></label>
                          <input
                            type="email"
                            className="form-control"
                            value={author.email || ''}
                            onChange={(e) => updateAuthor(author.id, 'email', e.target.value)}
                            placeholder="Enter email address"
                            required
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Affiliation <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control"
                            value={author.institution || ''}
                            onChange={(e) => updateAuthor(author.id, 'institution', e.target.value)}
                            placeholder="Enter affiliation/institution"
                            required
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Department</label>
                          <input
                            type="text"
                            className="form-control"
                            value={author.department || ''}
                            onChange={(e) => updateAuthor(author.id, 'department', e.target.value)}
                            placeholder="Enter department"
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Country <span className="text-danger">*</span></label>
                          <select
                            className="form-select"
                            value={author.country || ''}
                            onChange={(e) => updateAuthor(author.id, 'country', e.target.value)}
                            required
                          >
                            <option value="">Select Country</option>
                            {countries.map(country => (
                              <option key={country} value={country}>{country}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12 mb-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`corresponding_${author.id}`}
                              checked={author.isCorresponding}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setCorrespondingAuthor(author.id);
                                } else {
                                  updateAuthor(author.id, 'isCorresponding', false);
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor={`corresponding_${author.id}`}>
                              Corresponding Author
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 3:
        return (
          <div>
            <PaperFilesEdit
              paper={paper}
              formData={formData}
              handleFileChange={handleFileChange}
            />
            {/* <div className="text-end mt-3">
              <button
                type="button"
                className="btn btn-success"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div> */}
          </div>
        );
      case 4:
        return (
          <div>
            <h4 className="mb-3">
              <i className="bi bi-clipboard-check me-2"></i>
              Admin Notes
            </h4>
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Admin Only:</strong> These notes are internal and not visible to paper authors.
            </div>
            
            <div className="mb-3">
              <label htmlFor="adminNotes" className="form-label">
                Internal Notes
              </label>
              <textarea
                className="form-control"
                id="adminNotes"
                name="adminNotes"
                rows="10"
                value={formData.adminNotes || ''}
                onChange={handleChange}
                placeholder="Add notes about this paper submission. These notes are only visible to administrators and can include review comments, internal decisions, or any administrative information."
              ></textarea>
              <div className="form-text">
                <i className="bi bi-lock-fill me-1"></i>
                Use this space to record internal notes, review comments, or any administrative information about this paper.
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-pencil-square me-2"></i>
              Edit Paper: {paper?.manuscriptTitle}
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onHide}
              disabled={saving}
            ></button>
          </div>

          <div className="modal-body">
            {/* Section Navigation */}
            <div className="mb-4">
              <ul className="nav nav-pills nav-justified">
                <li className="nav-item">
                  <button
                    className={`nav-link ${currentSection === 1 ? 'active' : ''}`}
                    onClick={() => setCurrentSection(1)}
                    type="button"
                  >
                    1. Brief Description
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${currentSection === 2 ? 'active' : ''}`}
                    onClick={() => setCurrentSection(2)}
                    type="button"
                  >
                    2. Authors
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${currentSection === 3 ? 'active' : ''}`}
                    onClick={() => setCurrentSection(3)}
                    type="button"
                  >
                    3. Files
                  </button>
                </li>
                {!isUserContext && (
                  <li className="nav-item">
                    <button
                      className={`nav-link ${currentSection === 4 ? 'active' : ''}`}
                      onClick={() => setCurrentSection(4)}
                      type="button"
                    >
                      4. Admin Notes
                    </button>
                  </li>
                )}
              </ul>
            </div>

            {/* Error Display */}
            {error && (
              <div className="alert alert-danger" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {/* Loading Indicator */}
            {loading && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {/* Current Section Content */}
            {!loading && renderCurrentSection()}
          </div>

          <div className="modal-footer d-flex justify-content-between">
            <div>
              {currentSection > 1 && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={prevSection}
                  disabled={saving}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Previous
                </button>
              )}
            </div>

            <div>
              {currentSection < maxSection ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={nextSection}
                  disabled={saving}
                >
                  Next
                  <i className="bi bi-arrow-right ms-2"></i>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Save Changes
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaperEditModal;
